import React, { memo, useState, useRef, useEffect } from 'react';
import { LineChart, Loader2, Sparkles, Map, ThumbsUp, ThumbsDown, Database } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, prism } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { cn } from '../../lib/utils';
import { DynamicChart } from './Charts';
import PlotContainer from './PlotContainer';
import MapLayerCard from './MapLayerCard';
import InsightBlock from './InsightBlock';
import { submitFeedback } from '../../api/feedbackApi';
import { showErrorToast, showSuccessToast } from '../../lib/toast';
import { pickThinkingPhrase } from './thinkingPhrases';

/** Matches a single row in the streamed agent_steps list (spinner + label). */
const AGENT_STEP_ROW_CLASS =
  'flex items-center gap-2 text-xs text-gray-500/90 dark:text-gray-500';
const AGENT_STEP_SPINNER_CLASS = 'shrink-0 animate-spin text-gray-400 dark:text-gray-500';

const MAX_FEEDBACK_MESSAGE_CHARS = 3800;

function buildChatFeedbackMessage({ message, sentiment, timeSaved, issue, details }) {
  const parts = [
    `Chat response feedback: ${sentiment}`,
    `Message ID: ${message.id}`,
  ];

  if (timeSaved) {
    parts.push(`Time saved: ${timeSaved} minute${Number(timeSaved) === 1 ? '' : 's'}`);
  }
  if (issue) {
    parts.push(`Issue: ${issue}`);
  }
  if (details) {
    parts.push(`Details: ${details}`);
  }
  if (message.content) {
    parts.push(`Assistant message:\n${message.content}`);
  }
  if (message.sql) {
    parts.push(`SQL:\n${message.sql}`);
  }

  const feedbackMessage = parts.join('\n\n');
  return feedbackMessage.length > MAX_FEEDBACK_MESSAGE_CHARS
    ? `${feedbackMessage.slice(0, MAX_FEEDBACK_MESSAGE_CHARS - 3)}...`
    : feedbackMessage;
}

const Message = memo(({ message, onSuggestionClick, onAddLayer, onViewMapLayer, onLinkDataset }) => {
  const isBot = message.role === 'assistant';
  const [openingPhrase] = useState(() => pickThinkingPhrase());
  const [feedbackSelection, setFeedbackSelection] = useState(null); // 'thumbs_up', 'thumbs_down', or null - persists
  const [promptOpen, setPromptOpen] = useState(false); // Whether the feedback prompt is currently open
  const [timeSaved, setTimeSaved] = useState('');
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [otherMessage, setOtherMessage] = useState('');
  const [isFeedbackSubmitting, setIsFeedbackSubmitting] = useState(false);
  const feedbackPromptRef = useRef(null);
  const feedbackContainerRef = useRef(null);
  const isDark = document.documentElement.classList.contains('dark');
  const hasFeedbackMessageId = message.id !== undefined && message.id !== null;
  const hasFeedbackContent = Boolean(
    message.content ||
    message.chartData ||
    message.sql ||
    message.isMapResult ||
    message.suggestions?.length ||
    message.insight?.key_findings?.length
  );
  const showFeedback = isBot && hasFeedbackMessageId && !message.isLoading && hasFeedbackContent;

  // Handle click outside to dismiss feedback prompt (but keep the feedback selection)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        feedbackPromptRef.current &&
        !feedbackPromptRef.current.contains(event.target) &&
        feedbackContainerRef.current &&
        !feedbackContainerRef.current.contains(event.target)
      ) {
        // Only close the prompt, don't reset the feedback selection
        setPromptOpen(false);
      }
    };

    if (promptOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [promptOpen]);

  // Scroll prompt into view when it opens
  useEffect(() => {
    if (promptOpen && feedbackPromptRef.current) {
      setTimeout(() => {
        feedbackPromptRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  }, [promptOpen]);

  const handleThumbsUp = (e) => {
    e.stopPropagation();
    if (feedbackSelection === 'thumbs_up') {
      // Toggle off if already selected
      setFeedbackSelection(null);
      setPromptOpen(false);
      setTimeSaved('');
    } else {
      // Set selection and open prompt
      setFeedbackSelection('thumbs_up');
      setPromptOpen(true);
      // Clear any previous thumbs down data
      setSelectedIssue(null);
      setOtherMessage('');
    }
  };

  const handleThumbsDown = (e) => {
    e.stopPropagation();
    if (feedbackSelection === 'thumbs_down') {
      // Toggle off if already selected
      setFeedbackSelection(null);
      setPromptOpen(false);
      setSelectedIssue(null);
      setOtherMessage('');
    } else {
      // Set selection and open prompt
      setFeedbackSelection('thumbs_down');
      setPromptOpen(true);
      // Clear any previous thumbs up data
      setTimeSaved('');
    }
  };

  const submitChatFeedback = async ({ sentiment, timeSavedValue = '', issue = '', details = '' }) => {
    if (isFeedbackSubmitting) return;

    setIsFeedbackSubmitting(true);
    try {
      await submitFeedback({
        message: buildChatFeedbackMessage({
          message,
          sentiment,
          timeSaved: timeSavedValue.trim(),
          issue,
          details: details.trim(),
        }),
        category: sentiment === 'helpful' ? 'chat_thumbs_up' : 'chat_thumbs_down',
        pageUrl: `${window.location.origin}${window.location.pathname}${window.location.search}`,
      });
      showSuccessToast('Thanks for your feedback!');
      setPromptOpen(false);
      setTimeSaved('');
      if (sentiment === 'not helpful') {
        setOtherMessage('');
      }
    } catch {
      showErrorToast('Could not send feedback. Please try again.');
    } finally {
      setIsFeedbackSubmitting(false);
    }
  };

  const handleTimeSavedSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    submitChatFeedback({ sentiment: 'helpful', timeSavedValue: timeSaved });
  };

  const handleIssueSelect = (issue) => {
    setSelectedIssue(issue);
    // If "Other" is selected, don't close the prompt - show input instead
    if (issue === 'Other') {
      return;
    }
    submitChatFeedback({ sentiment: 'not helpful', issue });
  };

  const handleOtherIssueSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    submitChatFeedback({
      sentiment: 'not helpful',
      issue: 'Other',
      details: otherMessage,
    });
  };

  const thumbsDownOptions = [
    'Incorrect information',
    'Not helpful',
    'Too slow',
    'Other'
  ];

  return (
    <div className="flex flex-col mb-6" id={`message-${message.id}`}>
      <div className={cn(
        "flex w-full gap-4 animate-in fade-in slide-in-from-bottom-2 duration-300",
        !isBot && "justify-end"
      )}>
        <div className={cn(
          isBot
            ? "w-full text-gray-800 dark:text-gray-200"
            : "max-w-[85%] rounded-2xl p-4 shadow-sm bg-emerald-50 dark:bg-emerald-800/30 text-black dark:text-black rounded-tr-none"
        )}>
          {isBot && message.isLoading && (
            <div className="mb-3">
              {message.isMapResult && !message.status ? (
                <Loader2 size={14} className="animate-spin text-gray-400 dark:text-gray-500" />
              ) : (
                <div className={AGENT_STEP_ROW_CLASS}>
                  <Loader2 size={12} className={AGENT_STEP_SPINNER_CLASS} />
                  <span>{message.status || openingPhrase}</span>
                </div>
              )}
            </div>
          )}

          {message.content && (
            <div className={cn(
              "prose prose-base max-w-none",
              isBot
                ? "prose-gray dark:prose-invert prose-headings:text-gray-900 dark:prose-headings:text-gray-100 prose-p:text-gray-800 dark:prose-p:text-gray-200 prose-code:text-emerald-600 dark:prose-code:text-emerald-400 prose-code:bg-gray-100 dark:prose-code:bg-gray-800 prose-pre:bg-gray-100 dark:prose-pre:bg-gray-800 prose-a:text-emerald-600 dark:prose-a:text-emerald-400 hover:prose-a:text-emerald-700 dark:hover:prose-a:text-emerald-300"
                : "prose-headings:text-black dark:prose-headings:text-black prose-p:text-black dark:prose-p:text-black prose-code:text-emerald-800 dark:prose-code:text-emerald-700 prose-code:bg-emerald-200/50 dark:prose-code:bg-emerald-800/30 prose-pre:bg-emerald-200/30 dark:prose-pre:bg-emerald-800/30 prose-pre:border-emerald-400/30 dark:prose-pre:border-emerald-600/30 prose-a:text-emerald-800 dark:prose-a:text-emerald-700 hover:prose-a:text-emerald-900 dark:hover:prose-a:text-emerald-600 prose-blockquote:border-emerald-400/50"
            )}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {children}
                    </a>
                  ),
                  code: ({ node, inline, className, children, ...props }) => {
                    const match = /language-(\w+)/.exec(className || '');
                    const language = match ? match[1] : '';
                    const codeString = String(children).replace(/\n$/, '');

                    if (!inline && language) {
                      return (
                        <div className={cn(
                          "my-3 rounded-lg overflow-hidden",
                          isBot
                            ? "border border-gray-200 dark:border-gray-700"
                            : "border border-emerald-500/30"
                        )}>
                          <SyntaxHighlighter
                            language={language}
                            style={isDark ? vscDarkPlus : prism}
                            customStyle={{
                              margin: 0,
                              padding: '1rem',
                              fontSize: '1rem',
                              lineHeight: '1.6',
                              background: isBot
                                ? (isDark ? '#1f2937' : '#f3f4f6')
                                : 'rgba(16, 185, 129, 0.1)',
                            }}
                            codeTagProps={{
                              style: {
                                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                              }
                            }}
                            {...props}
                          >
                            {codeString}
                          </SyntaxHighlighter>
                        </div>
                      );
                    }

                    // Inline code - use prose styling
                    return <code className={className} {...props}>{children}</code>;
                  },
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}

          {message.visualization && !message.chartData && (
            <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700 flex items-center gap-3 text-gray-600 dark:text-gray-400">
              <LineChart size={20} className="text-emerald-500" />
              <span className="text-xs font-medium">Visualization: {message.visualization}</span>
            </div>
          )}
          {/* Modern Map Layer Card */}
          {message.isMapResult && message.layer_id && (
            <MapLayerCard
              layerId={message.layer_id}
              name={message.name || 'Map Layer'}
              sql={message.sql}
              onViewMap={() => onViewMapLayer && onViewMapLayer(message.layer_id)}
            />
          )}

          {/* Regular Chart/Plot Container */}
          {(message.chartData || message.sql) && !message.isMapResult && (
            <PlotContainer
              data={message.chartData}
              sql={message.sql}
              title={message.spec?.title || message.visualization}
              rowCount={message.rowCount !== undefined ? message.rowCount : (message.chartData ? message.chartData.length : 0)}
              isMapResult={false}
              tableOnly={!!message.tableOnly}
              spec={message.spec}
              isLoading={message.isLoading}
            >
              {message.chartData && message.spec && (
                <DynamicChart data={message.chartData} spec={message.spec} noContainer />
              )}
            </PlotContainer>
          )}

          {/* Insights — bullets only, under the chart, table, or map */}
          {isBot && (message.insightLoading || message.insight?.key_findings?.length > 0) && (
            <InsightBlock
              insight={message.insight}
              loading={!!message.insightLoading}
            />
          )}

          {isBot && message.linkDatasetHint && onLinkDataset && (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/80 px-3 py-2.5 dark:border-emerald-800/60 dark:bg-emerald-950/40">
              <div className="flex items-start gap-2.5">
                <Database size={16} className="mt-0.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-emerald-900 dark:text-emerald-100">
                    Link an imported dataset to this workspace to run queries.
                  </p>
                  <p className="mt-1 text-xs text-emerald-800/80 dark:text-emerald-200/80">
                    Use <span className="font-medium">+</span> in the Data panel, or link one below.
                  </p>
                  <button
                    type="button"
                    onClick={onLinkDataset}
                    className="mt-2.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 transition-colors"
                  >
                    Link dataset
                  </button>
                </div>
              </div>
            </div>
          )}

          {isBot && message.mappable && !message.isMapResult && (
            <div className="mt-4">
              <button
                onClick={() => {
                  if (onAddLayer && !message.mapLayerRequested) {
                    onAddLayer(message.originalQuery);
                  }
                }}
                disabled={message.isGeneratingMap || message.mapLayerRequested}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all",
                  "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400",
                  "hover:bg-emerald-100 dark:hover:bg-emerald-900/30",
                  "border border-emerald-200 dark:border-emerald-800",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {message.isGeneratingMap ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Generating map...</span>
                  </>
                ) : (
                  <>
                    <Map size={16} />
                    <span>Related map</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {isBot && message.suggestions && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-1.5 mb-2 text-xs text-gray-500 dark:text-gray-400">
            <Sparkles size={12} />
            <span>Suggestions</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {message.suggestions.map((q) => (
              <div
                key={q}
                onClick={() => onSuggestionClick(q)}
                className="px-2.5 py-1 text-xs text-black dark:text-white bg-gray-50 dark:bg-gray-800/30 hover:bg-gray-100 dark:hover:bg-gray-800/50 rounded-md cursor-pointer transition-colors"
              >
                {q}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Feedback buttons - only for completed assistant messages. */}
      {showFeedback && (
        <div ref={feedbackContainerRef} className="mt-2 relative">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleThumbsUp}
              disabled={isFeedbackSubmitting}
              className={cn(
                "p-1.5 rounded-md transition-all",
                feedbackSelection === 'thumbs_up'
                  ? "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20"
                  : "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/50",
                isFeedbackSubmitting && "opacity-50 cursor-not-allowed"
              )}
              title="Helpful"
              aria-label="Mark response as helpful"
            >
              <ThumbsUp size={16} />
            </button>
            <button
              type="button"
              onClick={handleThumbsDown}
              disabled={isFeedbackSubmitting}
              className={cn(
                "p-1.5 rounded-md transition-all",
                feedbackSelection === 'thumbs_down'
                  ? "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20"
                  : "text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/50",
                isFeedbackSubmitting && "opacity-50 cursor-not-allowed"
              )}
              title="Not helpful"
              aria-label="Mark response as not helpful"
            >
              <ThumbsDown size={16} />
            </button>
          </div>

          {/* Thumbs up feedback prompt */}
          {promptOpen && feedbackSelection === 'thumbs_up' && (
            <div
              ref={feedbackPromptRef}
              className="absolute top-full left-0 mt-2 z-50 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg min-w-[280px]"
              onClick={(e) => e.stopPropagation()}
            >
              <form onSubmit={handleTimeSavedSubmit} className="space-y-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  How much time did this save you? (minutes)
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  This response will be reviewed by our team.
                </p>
                <input
                  type="number"
                  min="0"
                  value={timeSaved}
                  onChange={(e) => setTimeSaved(e.target.value)}
                  placeholder="e.g., 5"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  autoFocus
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="submit"
                    disabled={isFeedbackSubmitting}
                    className="px-4 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isFeedbackSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Thumbs down feedback prompt */}
          {promptOpen && feedbackSelection === 'thumbs_down' && (
            <div
              ref={feedbackPromptRef}
              className="absolute top-full left-0 mt-2 z-50 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg min-w-[250px]"
              onClick={(e) => e.stopPropagation()}
            >
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                What went wrong?
              </label>
              <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                This response will be reviewed by our team.
              </p>
              <div className="space-y-2">
                {thumbsDownOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleIssueSelect(option)}
                    disabled={isFeedbackSubmitting}
                    className={cn(
                      "w-full text-left px-3 py-2 text-sm rounded-md transition-colors",
                      selectedIssue === option
                        ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                        : "bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700",
                      isFeedbackSubmitting && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    {option}
                  </button>
                ))}
              </div>
              {selectedIssue === 'Other' && (
                <form onSubmit={handleOtherIssueSubmit} className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <textarea
                    value={otherMessage}
                    onChange={(e) => setOtherMessage(e.target.value)}
                    placeholder="Please describe what went wrong..."
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                    autoFocus
                  />
                  <div className="mt-2 flex justify-end">
                    <button
                      type="submit"
                      disabled={!otherMessage.trim() || isFeedbackSubmitting}
                      className="px-4 py-1.5 text-xs font-medium bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isFeedbackSubmitting ? 'Submitting...' : 'Submit'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  return prevProps.message === nextProps.message &&
         prevProps.onSuggestionClick === nextProps.onSuggestionClick &&
         prevProps.onAddLayer === nextProps.onAddLayer &&
         prevProps.onViewMapLayer === nextProps.onViewMapLayer &&
         prevProps.onLinkDataset === nextProps.onLinkDataset;
});

export default Message;
